/**
 * Payment Error Code Mappings
 * Maps Coinflow error codes to user-friendly messages
 * 
 * These codes come from the backend PaymentErrorHandlerService
 * which parses Coinflow API errors and assigns standardized codes.
 */

export enum PaymentErrorCode {
  // User Blocked Errors
  USER_BLOCKED = 'USER_BLOCKED',
  SELLER_BLOCKED = 'SELLER_BLOCKED',

  // Customer Errors
  NO_CUSTOMER = 'NO_CUSTOMER',
  CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND',

  // Payment Errors
  PAYMENT_DECLINED = 'PAYMENT_DECLINED',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  CARD_EXPIRED = 'CARD_EXPIRED',
  INVALID_CARD = 'INVALID_CARD',
  CARD_NOT_SUPPORTED = 'CARD_NOT_SUPPORTED',

  // ACH Errors
  ACH_RETURNED = 'ACH_RETURNED',
  ACH_FAILED = 'ACH_FAILED',
  INVALID_ROUTING_NUMBER = 'INVALID_ROUTING_NUMBER',
  INVALID_ACCOUNT_NUMBER = 'INVALID_ACCOUNT_NUMBER',

  // 3DS Errors
  THREE_DS_FAILED = 'THREE_DS_FAILED',
  THREE_DS_CANCELLED = 'THREE_DS_CANCELLED',

  // Withdrawal Errors
  WITHDRAWAL_FAILED = 'WITHDRAWAL_FAILED',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  KYC_REQUIRED = 'KYC_REQUIRED',
  KYC_FAILED = 'KYC_FAILED',

  // Generic Errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  INVALID_REQUEST = 'INVALID_REQUEST',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Error code to user-friendly message mapping
 */
const ERROR_MESSAGES: Record<PaymentErrorCode | string, string> = {
  // User Blocked Errors
  [PaymentErrorCode.USER_BLOCKED]: 'Your account has been blocked. Please contact support.',
  [PaymentErrorCode.SELLER_BLOCKED]: 'Payment processing is temporarily unavailable. Please contact support.',

  // Customer Errors
  [PaymentErrorCode.NO_CUSTOMER]: 'Customer account not found. Please try again or contact support.',
  [PaymentErrorCode.CUSTOMER_NOT_FOUND]: 'Customer account not found. Please try again or contact support.',

  // Payment Errors
  [PaymentErrorCode.PAYMENT_DECLINED]: 'Your payment was declined. Please check your card details or try a different payment method.',
  [PaymentErrorCode.INSUFFICIENT_FUNDS]: 'Insufficient funds. Please check your account balance or use a different payment method.',
  [PaymentErrorCode.CARD_EXPIRED]: 'Your card has expired. Please use a different card.',
  [PaymentErrorCode.INVALID_CARD]: 'Invalid card information. Please check your card details and try again.',
  [PaymentErrorCode.CARD_NOT_SUPPORTED]: 'This card type is not supported. Please use a different payment method.',

  // ACH Errors
  [PaymentErrorCode.ACH_RETURNED]: 'Your bank transfer was returned. Please check your account details or contact your bank.',
  [PaymentErrorCode.ACH_FAILED]: 'Bank transfer failed. Please verify your account information and try again.',
  [PaymentErrorCode.INVALID_ROUTING_NUMBER]: 'Invalid routing number. Please check your bank account details.',
  [PaymentErrorCode.INVALID_ACCOUNT_NUMBER]: 'Invalid account number. Please check your bank account details.',

  // 3DS Errors
  [PaymentErrorCode.THREE_DS_FAILED]: 'Additional verification failed. Please try again.',
  [PaymentErrorCode.THREE_DS_CANCELLED]: 'Verification was cancelled. Please try again.',

  // Withdrawal Errors
  [PaymentErrorCode.WITHDRAWAL_FAILED]: 'Withdrawal failed. Please try again or contact support.',
  [PaymentErrorCode.INSUFFICIENT_BALANCE]: 'Insufficient balance. Please check your account balance.',
  [PaymentErrorCode.KYC_REQUIRED]: 'Identity verification is required. Please complete verification to continue.',
  [PaymentErrorCode.KYC_FAILED]: 'Identity verification failed. Please contact support for assistance.',

  // Generic Errors
  [PaymentErrorCode.NETWORK_ERROR]: 'Network error. Please check your connection and try again.',
  [PaymentErrorCode.TIMEOUT]: 'Request timed out. Please try again.',
  [PaymentErrorCode.INVALID_REQUEST]: 'Invalid payment request. Please check your information and try again.',
  [PaymentErrorCode.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again or contact support.',
};

/**
 * Get user-friendly error message from error code
 * @param errorCode - The error code from the backend
 * @param fallbackMessage - Fallback message if code is not found
 * @returns User-friendly error message
 */
export function getPaymentErrorMessage(
  errorCode?: string,
  fallbackMessage?: string
): string {
  if (!errorCode) {
    return fallbackMessage || 'An unexpected error occurred. Please try again.';
  }

  return ERROR_MESSAGES[errorCode] || fallbackMessage || 'An unexpected error occurred. Please try again or contact support.';
}

/**
 * Extract error code from error object
 * @param error - Error object (string, Error, or API error response)
 * @returns Error code if available, undefined otherwise
 */
export function extractErrorCode(error: any): string | undefined {
  if (!error) return undefined;

  // If error is a string, no code available
  if (typeof error === 'string') return undefined;

  // Check various possible locations for error code
  return (
    error.code ||
    error.response?.error ||
    error.response?.data?.error ||
    error.error
  );
}

/**
 * Extract error message from error object
 * @param error - Error object (string, Error, or API error response)
 * @returns Error message
 */
export function extractErrorMessage(error: any): string {
  if (!error) return 'An unexpected error occurred.';

  if (typeof error === 'string') return error;

  return (
    error.message ||
    error.response?.message ||
    error.response?.data?.message ||
    'An unexpected error occurred.'
  );
}

/**
 * Get formatted payment error with code and message
 * @param error - Error object
 * @returns Object with code and user-friendly message
 */
export function getPaymentError(error: any): {
  code?: string;
  message: string;
} {
  const code = extractErrorCode(error);
  const rawMessage = extractErrorMessage(error);
  const userMessage = getPaymentErrorMessage(code, rawMessage);

  return {
    code,
    message: userMessage,
  };
}

